import { useState, useEffect, useCallback } from "react";
import { useSession, signOut, type AuthSession, type User } from "../lib/auth-client";

interface UserConfig {
  id: string;
  user_id: string;
  dropcatch_client_id: string | null;
  dropcatch_client_secret: string | null;
  resend_api_key: string | null;
  google_api_key: string | null;
  email_notifications: boolean;
  notification_email: string | null;
}

interface UseAuthReturn {
  user: User | null;
  session: AuthSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEmailVerified: boolean;
  hasDropCatchCredentials: boolean;
  userConfig: UserConfig | null;
  logout: () => Promise<void>;
  refreshConfig: () => Promise<void>;
  updateConfig: (config: Partial<UserConfig>) => Promise<void>;
}

const API_BASE = import.meta.env.PROD
  ? 'https://zeropointdomains.com/api'
  : '/api';

export function useAuth(): UseAuthReturn {
  const { data: session, isPending } = useSession();
  const [userConfig, setUserConfig] = useState<UserConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);

  const user = session?.user as User | null;
  const isAuthenticated = !!user;
  const isAdmin = user?.isAdmin || false;
  const isEmailVerified = user?.emailVerified || false;
  const hasDropCatchCredentials = !!(userConfig?.dropcatch_client_id && userConfig?.dropcatch_client_secret);

  // Fetch user config when authenticated
  const refreshConfig = useCallback(async () => {
    if (!isAuthenticated) {
      setUserConfig(null);
      return;
    }

    setConfigLoading(true);
    try {
      const response = await fetch(`${API_BASE}/user/config`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUserConfig(data.config);
      }
    } catch (error) {
      console.error("Failed to fetch user config:", error);
    } finally {
      setConfigLoading(false);
    }
  }, [isAuthenticated]);

  // Load config when authenticated
  useEffect(() => {
    if (isAuthenticated && !userConfig && !configLoading) {
      refreshConfig();
    }
  }, [isAuthenticated, userConfig, configLoading, refreshConfig]);

  // Clear config when logged out
  useEffect(() => {
    if (!isAuthenticated) {
      setUserConfig(null);
    }
  }, [isAuthenticated]);

  const logout = useCallback(async () => {
    try {
      await signOut();
      setUserConfig(null);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, []);

  const updateConfig = useCallback(async (config: Partial<UserConfig>) => {
    try {
      const response = await fetch(`${API_BASE}/user/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(config),
      });

      if (response.ok) {
        const data = await response.json();
        setUserConfig(data.config);
      } else {
        throw new Error("Failed to update config");
      }
    } catch (error) {
      console.error("Failed to update config:", error);
      throw error;
    }
  }, []);

  return {
    user,
    session: session as AuthSession | null,
    isLoading: isPending || configLoading,
    isAuthenticated,
    isAdmin,
    isEmailVerified,
    hasDropCatchCredentials,
    userConfig,
    logout,
    refreshConfig,
    updateConfig,
  };
}

export default useAuth;
